import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
// import { EventClickArg, DateSelectArg } from '@fullcalendar/core';
import { useParams } from 'react-router-dom';
import { getFieldAvailability, getFieldDetail } from '@/services/fieldApiService';
import ScheduleModal from '@/components/forms/ScheduleModal';

const ScheduleView: React.FC = () => {
  const calendarRefDesktop = useRef<FullCalendar>(null);
  const calendarRefMobile = useRef<FullCalendar>(null);
  const [currentView, setCurrentView] = useState<'Month' | 'Week' | 'Day'>('Week');
  const [currentStartDate, setCurrentStartDate] = useState(new Date());
  const { slugField } = useParams<{ slugField?: string }>();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldInfo, setFieldInfo] = useState<{
    name?: string;
    type?: string;
    category?: string;
  } | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  // events state will be populated from API

  // const handleDateSelect = (selectInfo: DateSelectArg) => {
  //   const title = prompt('Enter event title:');
  //   const calendarApi = selectInfo.view.calendar;

  //   calendarApi.unselect(); // clear date selection

  //   if (title) {
  //     calendarApi.addEvent({
  //       id: String(Math.random()),
  //       title,
  //       start: selectInfo.startStr,
  //       end: selectInfo.endStr,
  //       backgroundColor: '#f97316',
  //       borderColor: '#f97316',
  //       textColor: '#ffffff',
  //     });
  //   }
  // };

  // const handleEventClick = (clickInfo: EventClickArg) => {
  //   if (confirm(`Are you sure you want to delete the event '${clickInfo.event.title}'`)) {
  //     clickInfo.event.remove();
  //   }
  // };

  const goToToday = () => {
    setCurrentStartDate(new Date());
    if (calendarRefDesktop.current) {
      calendarRefDesktop.current.getApi().gotoDate(new Date());
    }
    if (calendarRefMobile.current) {
      calendarRefMobile.current.getApi().gotoDate(new Date());
    }
  };

  // Fetch availability when slugField or arenaSlug changes
  useEffect(() => {
    const fetchAvailability = async () => {
      const arenaSlug = localStorage.getItem('arenaSlug') || '';
      if (!arenaSlug || !slugField) {
        setError('Missing arena or field identifier');
        setLoading(false);
        return;
      }

      // fetch field detail (name and type)
      try {
        const fieldRes = await getFieldDetail(arenaSlug, slugField);
        setFieldInfo({
          name: fieldRes.data.field.name,
          type: fieldRes.data.field.type,
          category: fieldRes.data.field.sport_category?.name,
        });
      } catch (err) {
        // non-blocking: show default if fails
        console.warn('Failed to fetch field detail', err);
      }

      try {
        setLoading(true);
        setError(null);
        const slots = await getFieldAvailability(arenaSlug, slugField);

        console.log('API Response (direct array):', slots);

        // API returns array directly with FullCalendar format
        if (Array.isArray(slots) && slots.length > 0) {
          setEvents(slots);

          // navigate calendar to the week/day that contains the first event
          try {
            const firstStart = new Date(slots[0].start);
            setCurrentStartDate(firstStart);
            if (calendarRefDesktop.current) {
              calendarRefDesktop.current.getApi().gotoDate(firstStart);
            }
            if (calendarRefMobile.current) {
              calendarRefMobile.current.getApi().gotoDate(firstStart);
            }
          } catch (e) {
            console.warn('Failed to parse first event start date', e);
          }
        } else {
          setEvents([]);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load availability');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [slugField]);

  const navigateDate = (direction: 'prev' | 'next') => {
    const newStartDate = new Date(currentStartDate);
    if (direction === 'prev') {
      newStartDate.setDate(newStartDate.getDate() - 7);
    } else {
      newStartDate.setDate(newStartDate.getDate() + 7);
    }
    setCurrentStartDate(newStartDate);

    if (calendarRefDesktop.current) {
      calendarRefDesktop.current.getApi().gotoDate(newStartDate);
    }
  };

  // Mobile navigation: move by 1 day instead of 7
  const navigateDateMobile = (direction: 'prev' | 'next') => {
    const newStartDate = new Date(currentStartDate);
    if (direction === 'prev') {
      newStartDate.setDate(newStartDate.getDate() - 1);
    } else {
      newStartDate.setDate(newStartDate.getDate() + 1);
    }
    setCurrentStartDate(newStartDate);

    if (calendarRefMobile.current) {
      calendarRefMobile.current.getApi().gotoDate(newStartDate);
    }
  };

  // change FullCalendar view when currentView changes
  useEffect(() => {
    const viewMap: Record<string, string> = {
      Month: 'dayGridMonth',
      Week: 'timeGridWeek',
      Day: 'timeGridDay',
    };
    const targetView = viewMap[currentView] || 'timeGridWeek';
    try {
      // Only change desktop calendar view here. Mobile will always use single-day view.
      if (calendarRefDesktop.current) {
        calendarRefDesktop.current.getApi().changeView(targetView);
      }
    } catch (e) {
      // ignore errors from changeView
      console.warn('changeView failed', e);
    }
  }, [currentView]);

  const formatDateRange = () => {
    const endDate = new Date(currentStartDate.getTime() + 6 * 24 * 60 * 60 * 1000);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${currentStartDate.toLocaleDateString('en-US', options)} — ${endDate.toLocaleDateString('en-US', options)}, ${currentStartDate.getFullYear()}`;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="hidden md:block">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <Icon icon="mdi:home" className="w-4 h-4" />
          <span className="text-primary-500">Arena</span>
          <Icon icon="mdi:chevron-right" className="w-4 h-4" />
          <span>Schedule View</span>
        </div>

        {/* Header with field info */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-lg overflow-hidden flex items-center justify-center">
            <Icon icon="mdi:soccer-field" className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {fieldInfo?.name ?? 'Lapangan Futsal Jaya 1'}
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Icon icon="mdi:map-marker" className="w-4 h-4" />
              <span>
                {fieldInfo?.category ? fieldInfo.category.replace('_', ' ') : 'Lapangan Bola'}
              </span>
            </div>
          </div>
        </div>

        {/* Calendar Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {/* Navigation buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateDate('prev')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Icon icon="mdi:chevron-left" className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigateDate('next')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Icon icon="mdi:chevron-right" className="w-5 h-5" />
              </button>
              <button
                onClick={goToToday}
                className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Today
              </button>
            </div>

            {/* Date range */}
            <h2 className="text-2xl font-semibold text-gray-900">{formatDateRange()}</h2>
          </div>

          <div className="flex items-center gap-3">
            {/* View toggle buttons */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              {(['Month', 'Week', 'Day'] as const).map((view) => (
                <button
                  key={view}
                  onClick={() => setCurrentView(view)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-r border-gray-300 last:border-r-0 ${
                    currentView === view
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {view}
                </button>
              ))}
            </div>

            {/* Add Schedule button */}
            <button
              onClick={() => setIsScheduleModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Icon icon="mdi:plus" className="w-5 h-5" />
              Add Schedule
            </button>
          </div>
        </div>
      </div>

      {/* FullCalendar - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Loading schedule data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <Icon icon="mdi:alert-circle" className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <div className="text-red-600 font-medium mb-2">Failed to load schedule</div>
            <div className="text-gray-500 text-sm">{error}</div>
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center">
            <Icon icon="mdi:calendar-blank" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <div className="text-gray-600 font-medium mb-2">No schedule data available</div>
            <div className="text-gray-500 text-sm">
              There are no scheduled events for this field.
            </div>
          </div>
        ) : (
          <FullCalendar
            ref={calendarRefDesktop}
            plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
            headerToolbar={false} // We'll use custom header
            views={{
              customWeek: {
                type: 'timeGrid',
                duration: { days: 7 },
              },
            }}
            // initialView is only used on mount; we'll also call changeView when `currentView` changes
            initialView="timeGridWeek"
            initialDate={currentStartDate.toISOString()}
            key={`${currentView}-${currentStartDate.toISOString()}`} // Force re-render when date or view changes
            allDaySlot={false}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            slotDuration="01:00:00"
            slotLabelInterval="01:00:00"
            events={events}
            // selectable={true}
            selectMirror={true}
            // select={handleDateSelect}
            // eventClick={handleEventClick}
            // editable={true}
            dayMaxEvents={true}
            height="auto"
            contentHeight={600}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            dayHeaderFormat={{
              weekday: 'short',
              month: 'numeric',
              day: 'numeric',
            }}
            expandRows={true}
            stickyHeaderDates={true}
            nowIndicator={true}
            scrollTime="06:00:00"
            eventDisplay="block"
            displayEventTime={true}
            eventContent={(eventInfo) => (
              <div className="fc-event-main p-1">
                <div className="fc-event-title font-medium text-sm text-white">
                  {eventInfo.event.title}
                </div>
                <div className="fc-event-time text-xs opacity-90 text-white">
                  {eventInfo.timeText}
                </div>
                {eventInfo.event.extendedProps?.price && (
                  <div className="fc-event-price text-xs opacity-90 text-white">
                    Rp {eventInfo.event.extendedProps.price}
                  </div>
                )}
              </div>
            )}
          />
        )}
      </div>

      {/* Mobile header: field info, Add button, and per-day navigator */}
      <div className="md:hidden mb-4">
        <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg overflow-hidden flex items-center justify-center">
              <Icon icon="mdi:soccer-field" className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-sm">
                {fieldInfo?.name ?? 'Lapangan Futsal Jaya 1'}
              </div>
              <div className="text-xs text-gray-600">
                {fieldInfo?.category ? fieldInfo.category.replace('_', ' ') : 'Lapangan Bola'}
              </div>
            </div>
          </div>
          {/* <div>
            <button className="p-2 rounded-md hover:bg-gray-100">
              <Icon icon="mdi:chevron-down" className="w-5 h-5 text-gray-600" />
            </button>
          </div> */}
        </div>

        <button
          onClick={() => setIsScheduleModalOpen(true)}
          className="w-full mt-3 bg-primary-500 text-white rounded-lg py-3 flex items-center justify-center gap-2 shadow-md"
        >
          <Icon icon="mdi:plus" className="w-5 h-5" />
          <span className="font-medium">Tambah Jadwal</span>
        </button>

        <div className="mt-3 relative">
          <div className="flex items-center justify-between py-2">
            <div>
              <button
                onClick={() => navigateDateMobile('prev')}
                className="bg-white p-2 rounded-md shadow-md border border-gray-200 hover:shadow-lg"
                aria-label="Previous day"
              >
                <Icon icon="mdi:chevron-left" className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div>
              <button
                onClick={() => navigateDateMobile('next')}
                className="bg-white p-2 rounded-md shadow-md border border-gray-200 hover:shadow-lg"
                aria-label="Next day"
              >
                <Icon icon="mdi:chevron-right" className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="text-sm font-medium text-gray-700">
              {new Date(currentStartDate).toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>
        </div>
      </div>

      {/* FullCalendar - Mobile (single day) */}
      <div className="md:hidden bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-4 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Loading schedule data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <Icon icon="mdi:alert-circle" className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <div className="text-red-600 font-medium mb-1">Failed to load schedule</div>
            <div className="text-gray-500 text-sm">{error}</div>
          </div>
        ) : events.length === 0 ? (
          <div className="p-4 text-center">
            <Icon icon="mdi:calendar-blank" className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <div className="text-gray-600 font-medium mb-1">No schedule data available</div>
            <div className="text-gray-500 text-sm">
              There are no scheduled events for this field.
            </div>
          </div>
        ) : (
          <FullCalendar
            ref={calendarRefMobile}
            plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
            headerToolbar={false}
            initialView="timeGridDay"
            initialDate={currentStartDate.toISOString()}
            key={`mobile-${currentStartDate.toISOString()}`}
            allDaySlot={false}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            slotDuration="01:00:00"
            slotLabelInterval="01:00:00"
            events={events}
            selectMirror={true}
            dayMaxEvents={true}
            height="auto"
            contentHeight={600}
            slotLabelFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
            eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
            dayHeaderFormat={{ weekday: 'short', month: 'numeric', day: 'numeric' }}
            expandRows={true}
            stickyHeaderDates={true}
            nowIndicator={true}
            scrollTime="06:00:00"
            eventDisplay="block"
            displayEventTime={true}
            eventContent={(eventInfo) => (
              <div className="fc-event-main p-1">
                <div className="fc-event-title font-medium text-sm text-white">
                  {eventInfo.event.title}
                </div>
                <div className="fc-event-time text-xs opacity-90 text-white">
                  {eventInfo.timeText}
                </div>
                {eventInfo.event.extendedProps?.price && (
                  <div className="fc-event-price text-xs opacity-90 text-white">
                    Rp {eventInfo.event.extendedProps.price}
                  </div>
                )}
              </div>
            )}
          />
        )}
      </div>

      {/* Schedule Modal - derive simple sessions list from events if possible */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        arenaSlug={localStorage.getItem('arenaSlug') || undefined}
        fieldSlug={slugField}
        onAddSchedule={(createdEvents) => {
          // append to events state
          setEvents((prev) => [...prev, ...createdEvents]);
        }}
      />
    </div>
  );
};

export default ScheduleView;
